import * as React from "react";

import type { SideNavThemeTokens, SideNavUserMenuOption } from "../SideNav/types";

import { cn } from "../../../lib/utils";

import { Icon } from "../../atoms/Icon";
import { PopoverContent } from "../../molecules/Popover";
import { buildSideNavThemeStyle } from "../SideNav/buildThemeStyle";

/** Shared account-menu body for the SideNav footer and TopNav avatar; consumers own the Popover/trigger. */

const userMenuRowBaseClassName =
	"flex w-full cursor-pointer appearance-none items-center gap-3 border-0 bg-transparent px-4 py-2 text-left text-paragraph-3 no-underline [font-family:inherit] transition-colors focus:outline-none";

const userMenuRowClassName = (isThemed: boolean) =>
	cn(
		userMenuRowBaseClassName,
		isThemed
			? "text-[color:var(--sidenav-fg)] hover:bg-[color:var(--sidenav-hover-bg)] focus-visible:bg-[color:var(--sidenav-hover-bg)]"
			: "text-neutral-9 hover:bg-neutral-1 focus-visible:bg-neutral-1"
	);

const UserMenuOptionIcon = ({
	icon,
	iconClassName,
}: {
	icon: NonNullable<SideNavUserMenuOption["icon"]>;
	iconClassName?: SideNavUserMenuOption["iconClassName"];
}) => (
	<span aria-hidden className="flex size-4 shrink-0 items-center justify-center">
		<Icon icon={icon} size={16} color={null} dangerouslySetClassName={iconClassName} />
	</span>
);

const UserMenuOptionRow = ({
	option,
	onSelect,
	testIdPrefix,
	isThemed,
}: {
	option: SideNavUserMenuOption;
	onSelect: () => void;
	testIdPrefix: string;
	isThemed: boolean;
}) => {
	const handleClick = (event: React.MouseEvent<HTMLAnchorElement | HTMLButtonElement>) => {
		option.onClick?.(event);
		onSelect();
	};
	const iconNode = option.icon ? (
		<UserMenuOptionIcon icon={option.icon} iconClassName={option.iconClassName} />
	) : null;
	const rowClassName = userMenuRowClassName(isThemed);
	if (option.href) {
		return (
			<a
				href={option.href}
				className={cn(rowClassName, option.className)}
				onClick={handleClick}
				role="menuitem"
				target={option.isExternal ? "_blank" : undefined}
				rel={option.isExternal ? "noopener noreferrer" : undefined}
				data-testid={option["data-testid"] ?? `${testIdPrefix}-${option.id}`}
			>
				{iconNode}
				<span className="min-w-0 flex-1 truncate">{option.label}</span>
			</a>
		);
	}
	return (
		<button
			type="button"
			className={cn(rowClassName, option.className)}
			onClick={handleClick}
			role="menuitem"
			data-testid={`${testIdPrefix}-${option.id}`}
		>
			{iconNode}
			<span className="min-w-0 flex-1 truncate">{option.label}</span>
		</button>
	);
};

const getEnabledUserMenuOptions = (menu: HTMLElement) =>
	Array.from(menu.querySelectorAll<HTMLElement>('[role="menuitem"]')).filter(
		(option) => !option.matches("[disabled], [aria-disabled='true']")
	);

const focusUserMenuOption = (menu: HTMLElement, nextIndex: number) => {
	const options = getEnabledUserMenuOptions(menu);
	if (options.length === 0) return;
	options[(nextIndex + options.length) % options.length].focus();
};

type PopoverContentProps = React.ComponentPropsWithoutRef<typeof PopoverContent>;

export type UserMenuProps = {
	options: SideNavUserMenuOption[];
	onSelect: () => void;
	side?: PopoverContentProps["side"];
	align?: PopoverContentProps["align"];
	sideOffset?: number;
	avoidCollisions?: boolean;
	forceMount?: true;
	onMouseEnter?: React.MouseEventHandler<HTMLDivElement>;
	onMouseLeave?: React.MouseEventHandler<HTMLDivElement>;
	/** Rendered above the options (TopNav background-jobs mount target). */
	children?: React.ReactNode;
	optionTestIdPrefix?: string;
	"data-testid"?: string;
	/** When set, paints the menu with the SideNav/TopNav theme tokens; omit for the default white surface. */
	theme?: SideNavThemeTokens;
};

export const UserMenu = ({
	options,
	onSelect,
	side = "bottom",
	align = "end",
	sideOffset = 8,
	avoidCollisions = true,
	forceMount,
	onMouseEnter,
	onMouseLeave,
	children,
	optionTestIdPrefix = "user-menu-option",
	"data-testid": dataTestId = "user-menu",
	theme,
}: UserMenuProps) => {
	const isThemed = Boolean(theme);
	const themeStyle = React.useMemo(() => {
		const base = buildSideNavThemeStyle(theme);
		if (!base) return undefined;
		const vars = { ...base };
		delete vars.backgroundColor;
		delete vars.color;
		return vars;
	}, [theme]);

	const handleKeyDown = (event: React.KeyboardEvent<HTMLElement>) => {
		const menu = event.currentTarget;
		const enabled = getEnabledUserMenuOptions(menu);
		if (enabled.length === 0) return;
		const currentIndex = enabled.findIndex((option) => option === document.activeElement);

		if (event.key === "ArrowDown") {
			event.preventDefault();
			focusUserMenuOption(menu, currentIndex === -1 ? 0 : currentIndex + 1);
		} else if (event.key === "ArrowUp") {
			event.preventDefault();
			focusUserMenuOption(menu, currentIndex === -1 ? enabled.length - 1 : currentIndex - 1);
		} else if (event.key === "Home") {
			event.preventDefault();
			focusUserMenuOption(menu, 0);
		} else if (event.key === "End") {
			event.preventDefault();
			focusUserMenuOption(menu, enabled.length - 1);
		}
	};

	return (
		<PopoverContent
			forceMount={forceMount}
			side={side}
			align={align}
			sideOffset={sideOffset}
			avoidCollisions={avoidCollisions}
			// z-[101] beats admin chrome (z-98/99).
			className={cn(
				"z-[101] w-56 overflow-hidden rounded-lg border p-0 shadow-lg",
				"transition-opacity duration-150 data-[state=closed]:pointer-events-none data-[state=closed]:invisible data-[state=closed]:opacity-0",
				isThemed
					? "border-[color:var(--sidenav-divider)] bg-[color:var(--sidenav-bg)] text-[color:var(--sidenav-fg)]"
					: "border-neutral-3 bg-neutral-0"
			)}
			style={themeStyle}
			role="menu"
			aria-label="User menu"
			data-testid={dataTestId}
			onKeyDown={handleKeyDown}
			onMouseEnter={onMouseEnter}
			onMouseLeave={onMouseLeave}
		>
			<div className="py-2">
				{children}
				{options.map((option, idx) => (
					<React.Fragment key={option.id}>
						{option.divider === "before" && idx > 0 ? (
							<div
								aria-hidden
								className={cn(
									"my-1 h-px",
									isThemed ? "bg-[color:var(--sidenav-divider)]" : "bg-neutral-2"
								)}
							/>
						) : null}
						<UserMenuOptionRow
							option={option}
							onSelect={onSelect}
							testIdPrefix={optionTestIdPrefix}
							isThemed={isThemed}
						/>
					</React.Fragment>
				))}
			</div>
		</PopoverContent>
	);
};

UserMenu.displayName = "UserMenu";
